import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Group, Layout, Page, Tab, Tree } from "@/comn/components";
import { useFetch, useForm, useModal, useToast, useTab, useResource, useTree, usePage, useStore } from "@/comn/hooks";
import { APIS, SF_ORG_MGMT_INFO, SF_ORG_DEPT_INFO, URLS } from "@/tra/tancis/ptli/usr/org/services/PtliOrgService";

/*
 * Modifying and Deleting Organization Management
 * !== 기관 관리 수정 및 삭제 ==!
 */
export const PTLI0916003S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const tree = useTree(); // Tree Hook !== Tree Hook ==!
    const { orgCd } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    useResource({
        defaultSchema: [{ area: "orgDeptCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization Information Form !== 기관 정보 form ==!
        orgInfo: useForm({
            defaultSchema: SF_ORG_MGMT_INFO,
            defaultValues: {},
        }),
        // Organization Information Form !== 기관의 부서 정보 form ==!
        deptInfo: useForm({
            defaultSchema: SF_ORG_DEPT_INFO,
            defaultValues: {},
        }),
    };

    // Organization, Department Tab !== 기관,부서 탭 ==!
    const tab = useTab({
        defaultSchema: {
            id: "tab",
            schema: [{ label: "L_ORG" }, { label: "L_ORG_DEPT" }],
        },
    });

    // TIN 값 중복 확인 전, 기존 TIN에서 변경이 됬는지 확인을 위해 변수 정의
    const prev_tin = useRef<string>("");

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check Organization Information !== 기관 정보 조회 ==!
        getOrgInfo: useFetch({
            api: (data) => APIS.getOrgInfo(orgCd),
            enabled: !!orgCd,
            onSuccess: (data) => {
                const orgInfo = data.orgInfo.content;
                form.orgInfo.setValues(orgInfo);

                prev_tin.current = form.orgInfo.getValue("tin") ?? "";
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00018" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        // Check Organization Department Information !== 기관의 부서 정보 조회 ==!
        getDeptInfo: useFetch({
            api: (data) => APIS.getOrgDeptInfo(orgCd, data.deptCd),
            onSuccess: (data) => {
                const deptInfo = data.deptInfo.content;
                form.deptInfo.setValues(deptInfo);
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Update Organization Information !== 기관 정보 수정 ==!
        updateOrgInfo: useFetch({
            api: (info: any) => APIS.updateOrgInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00059" }); // Successfully updated. !== 정상적으로 수정되었습니다.==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00022" }), // Failed to update. !== 수정이 실패하였습니다.==!
        }),
        // Save Organization Department Information !== 기관의 부서 정보 저장 ==!
        saveOrgDeptInfo: useFetch({
            api: (info: any) => APIS.saveOrgDeptInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00059" }); // Successfully updated. !== 정상적으로 수정되었습니다.==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00022" }), // Failed to update. !== 수정이 실패하였습니다.==!
        }),
        // Delete Organization Information !== 기관 정보 삭제 ==!
        deleteOrgInfo: useFetch({
            api: (orgCd: any) => APIS.deleteOrgInfo(orgCd),
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00060" }); // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                navigate(URLS.ptli0916001q);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00023" }), // Failed to remove. !== 삭제가 실패하였습니다. ==!
        }),
        // Check Department List of the Organization !== 기관의 부서 목록 조회 ==!
        getDeptList: useFetch({
            api: () => APIS.getOrgDeptList(orgCd),
            onSuccess: (data) => {
                const deptList = data["deptList"].content;
                tree.setData([
                    {
                        name: "ROOT",
                        deptPath: "ROOT",
                        children: handler.createTreeData(deptList, [], 1, ""),
                    },
                ]);

                tree.openTree();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // 조회가 실패하였습니다.
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        initDeptForm: () => {
            form.deptInfo.setValue("deptPath", "ROOT");
        },
        // Click List Button !== 목록 버튼 클릭 ==!
        onListBtnClick: () => navigate(URLS.ptli0916001q),

        // onUpdateBtnClick: form.orgInfo.handleSubmit(
        //     () => {
        //         modal.openModal({
        //             content: "msg.com.00050", // Confirm update? !== 수정하시겠습니까? ==!
        //             onConfirm: () => fetch.updateOrgInfo.fetch(form.orgInfo.getValues()),
        //         });
        //     },
        //     () => toast.showToast({ type: "warning", content: "msg.00002" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
        // ),

        // Click Update Button !== 수정 버튼 클릭 ==!
        onUpdateBtnClick: async () => {
            const regex = /^\d{9}$/; // TIN을 체크하는 정규식 1. 숫자로만 이루어짐, 2. 9자리수로 이루어져야함
            const data = form.orgInfo.getValues();
            // 정규화를 통한 TIN 조건 처리
            if (!regex.test(form.orgInfo.getValue("tin"))) {
                modal.openModal({
                    content: "msg.esw.00070", // TIN must be 9 digits. ? !== TIN은 9자리여야 합니다. ? ==!
                });
                return;
            }

            // TIN 중복 여부 처리
            const resp = await APIS.getOrgTinDuplCheck(form.orgInfo.getValue("tin"));
            const isDup = resp.data.orgTinDuplCheck;

            // 이전 TIN과 비교하여 변경된 사실이 있을 때, 중복을 검사하도록 설정
            if (prev_tin.current !== form.orgInfo.getValue("tin")) {
                if (isDup.content) {
                    modal.openModal({ content: "msg.ptl.00770" }); // This TIN number is already in use. ? !== 이미 사용 중인 TIN 번호입니다. ? ==!
                    return;
                }
            }

            form.orgInfo.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.com.00050", // Confirm update? !== 수정하시겠습니까? ==!
                        onConfirm: () => {
                            fetch.updateOrgInfo.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        onUpdateOrgDeptBtnClick: form.deptInfo.handleSubmit(
            () => {
                form.deptInfo.setValue("orgCd", orgCd);
                modal.openModal({
                    content: "msg.com.00050", // Confirm update? !== 수정하시겠습니까? ==!
                    onConfirm: () => fetch.saveOrgDeptInfo.fetch(form.deptInfo.getValues()),
                });
            },
            () => toast.showToast({ type: "warning", content: "msg.00002" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
        ),
        // Click Delete Button !== 삭제 버튼 클릭 ==!
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.com.00049", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => fetch.deleteOrgInfo.fetch(form.orgInfo.getValue("orgCd")),
            });
        },
        // Click Department Tree !== 기관의 부서 트리 클릭 ==!
        onTreeClick: (treeInfo: any) => {
            handler.initDeptForm();
            fetch.getDeptInfo.fetch(treeInfo);
        },

        // create Department Tree Data  !== 기관의 부서 Tree 데이터 생성 ==!
        createTreeData: (deptList: any, newList: any, level: any, deptCd: any) => {
            const tempList = deptList
                .map((item: any) => {
                    return {
                        name: comnUtils.replaceEmpty(item.deptNm),
                        checkbox: false,
                        deptCd: comnUtils.replaceEmpty(item.deptCd),
                        deptNm: comnUtils.replaceEmpty(item.deptNm),
                        deptLvl: comnUtils.replaceEmpty(item.deptLvl, 0),
                        deptIdtnOrd: comnUtils.replaceEmpty(item.deptIdtnOrd, 0),
                        uprDeptCd: comnUtils.replaceEmpty(item.uprDeptCd),
                        orgCd: comnUtils.replaceEmpty(item.orgCd),
                    };
                })
                .filter((item: any) => item["deptLvl"] === level && item["uprDeptCd"] === deptCd);

            if (tempList.length === 0) return newList;

            tempList.forEach((item: any) => {
                if (!item.children) item.children = [];
                handler.createTreeData(deptList, item.children, item["deptLvl"] + 1, item["deptCd"]);
                newList.push(item);
            });
            return newList;
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOrgInfo.fetch();
        form.deptInfo.setEditable(false);
        handler.initDeptForm();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group>
                        <Group.Body>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.orgRegnDvnCd}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.orgAbrtNm}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.orgNm}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.tin}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.hgovNm}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.orgStatCd}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.rprsTelno}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.rprsFaxNo}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.lpcoIssuYn}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.postAddr}></Group.Control>
                                </Group.Row>
                            </Group.Section>
                            <Layout direction="row">
                                <Layout.Left>
                                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                                </Layout.Left>
                                <Layout.Right>
                                    <Layout.Button role={"save"} onClick={handler.onUpdateBtnClick} />
                                </Layout.Right>
                            </Layout>
                        </Group.Body>
                    </Group>
                </Group.Body>
            </Group>
        </Page>
    );
};
